import {
  Bell,
  Boxes,
  Bug,
  ChartColumn,
  ChevronDown,
  ClipboardList,
  ExternalLink,
  LayoutDashboard,
  Menu,
  Pencil,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Star,
  TrendingUp,
  Type,
  Upload,
  X,
} from 'lucide-react';
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import type { ForqModuleManifest, NavigationIcon } from '@forq/contracts';
import { usePlatform } from '@forq/platform';
import { emptyUiLayout, textOverride, type UiLayout } from '@forq/platform-data';
import { useCustomize } from '../customize/CustomizeContext';
import { NavTermsDialog, type NavTermGroup } from './NavTermsDialog';
import { TextRenameLayer } from '../customize/TextRenameLayer';
import { TEXT_TERM_PREFIX, textOverrideMap, useApplyTextOverrides } from '../customize/textOverrides';

interface AppShellProps extends PropsWithChildren {
  readonly modules: readonly ForqModuleManifest[];
  /** Opens FORQ Command (global search). Wired to the topbar search and Ctrl/⌘+K. */
  readonly onOpenCommand?: () => void;
  /** Opens FORQ Signal (task center). Wired to the topbar bell. */
  readonly onOpenSignal?: () => void;
  /** Count of open tasks for the active tenant — shown as the bell badge. */
  readonly signalCount?: number;
  /** Opens FORQY (contextual help/assistant dock). Wired to the topbar FORQY button. */
  readonly onOpenForqy?: () => void;
  /**
   * Öffnet „Fehler melden".
   *
   * Bewusst OHNE Rechteprüfung, anders als FORQY: melden darf jeder, der
   * angemeldet ist. Genau die Kollegen mit den wenigsten Rechten stoßen auf
   * die interessantesten Fehler — wer erst ein Recht braucht, meldet nicht.
   * Ist die Rückmeldung nicht eingerichtet, wird der Knopf gar nicht gesetzt.
   */
  readonly onReportBug?: () => void;
}

interface WorkspaceTab {
  readonly route: string;
  readonly label: string;
}

const categoryOrder = ['start', 'masterdata', 'operations', 'commercial', 'collaboration', 'system'] as const;
const categoryLabels: Readonly<Record<string, string>> = {
  start: 'Start',
  masterdata: 'Stammdaten',
  operations: 'Operatives Geschäft',
  commercial: 'Kaufmännisch & Vertrieb',
  collaboration: 'Zusammenarbeit',
  system: 'System',
};

// Text-Keys für die mandantenweite Umbenennung (Frontend-Customizer, page_key "terms"): je Navigations-
// Eintrag und je Kategorie ein Schlüssel. Leere/unveränderte Overrides zählen als „Standard".
const NAV_TERMS_PAGE_KEY = 'terms';
const navTermKey = (id: string) => `nav:${id}`;
const navGroupTermKey = (category: string) => `navgroup:${category}`;
const shellTermKey = (id: string) => `shell:${id}`;

/**
 * Die einfarbigen Zeichen aus dem Icon Pack sind Bilder. Sie werden als CSS-Maske gezeichnet und
 * nehmen damit `currentColor` an -- sonst blieben sie in jedem Zustand gleich hell, auch im
 * aktiven Eintrag, wo alles andere rot ist.
 *
 * Diese Menge wird von `packages/ui/src/styles/navIcons.test.ts` gegen shell.css abgeglichen.
 */
export const BILD_ICONS = new Set<NavigationIcon>([
  'artikel', 'dashboard', 'dokumente', 'ersatzteile', 'feedback', 'finanzen', 'kunden', 'lager',
  'lieferanten', 'maschinen', 'messenger', 'mietpark', 'service', 'studio', 'vorgaenge',
]);

/**
 * FORQY ist die Ausnahme: das Maskottchen lebt von seinen roten Augen, seinem Mund und dem roten
 * Bogen. Als Maske waere davon nichts uebrig -- eine Maske kennt nur durchsichtig oder nicht,
 * das Gesicht faende sich als Loch im Kopf wieder. Es wird deshalb als richtiges Bild gezeichnet
 * und bleibt in jedem Zustand gleich bunt. Bei einem Maskottchen ist das kein Bruch, sondern der
 * Zweck.
 *
 * Eine Ausnahme hat auch diese Ausnahme: im erzwungenen Kontrastmodus wird FORQY wieder als
 * Maske gezeichnet. Es ist zu 71 % weiss, und dort ist der Grund hell -- das Bild waere fast
 * unsichtbar. Die Regel dazu steht am Ende des Zeichen-Abschnitts in shell.css.
 */
export const FARBIGE_ICONS = new Set<NavigationIcon>(['forqy']);

function NavigationIconView({ icon }: { readonly icon?: NavigationIcon }) {
  if (icon && BILD_ICONS.has(icon)) {
    return <span className={`forq-nav-icon forq-nav-icon--${icon}`} aria-hidden />;
  }
  if (icon && FARBIGE_ICONS.has(icon)) {
    return <span className={`forq-nav-icon forq-nav-icon--vollfarbig forq-nav-icon--${icon}`} aria-hidden />;
  }
  // Fuer diese Bereiche gibt es noch kein eigenes Zeichen. Sie tragen eine Strichgrafik, die
  // wenigstens ihr Thema nennt -- die fruehere Sammelkiste stand neben dem Artikel-Karton und
  // war von ihm nicht zu unterscheiden.
  if (icon === 'auswertung') return <ChartColumn size={16} />;
  if (icon === 'verkauf') return <TrendingUp size={16} />;
  if (icon === 'dienstleistungen') return <ClipboardList size={16} />;
  if (icon === 'datenuebernahme') return <Upload size={16} />;
  if (icon === 'admin') return <ShieldCheck size={16} />;
  if (icon === 'module') return <Boxes size={16} />;
  return <LayoutDashboard size={16} />;
}

function fallbackRouteLabel(pathname: string): string {
  const segment = pathname.split('/').filter(Boolean).at(-1) ?? 'Arbeitsbereich';
  return segment.charAt(0).toLocaleUpperCase('de') + segment.slice(1);
}

// Persönliche Navigations-Favoriten -- pro Gerät im Browser, damit man die lange Seitenleiste auf die
// wirklich oft genutzten Einträge verkürzen kann (kein Hoch-und-runter-Scrollen mehr).
const FAVORITES_KEY = 'forq.nav-favorites.v1';
const MAX_FAVORITES = 5;

function readFavorites(): readonly string[] {
  try {
    const raw = globalThis.localStorage?.getItem(FAVORITES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string').slice(0, MAX_FAVORITES) : [];
  } catch {
    return [];
  }
}

export function AppShell({ children, modules, onOpenCommand, onOpenSignal, signalCount = 0, onOpenForqy, onReportBug }: AppShellProps) {
  const platform = usePlatform();
  const location = useLocation();
  const navigate = useNavigate();
  const [workspaceTabs, setWorkspaceTabs] = useState<readonly WorkspaceTab[]>([]);
  const [favorites, setFavorites] = useState<readonly string[]>(() => readFavorites());

  // Mandantenweite Begriffs-Umbenennung: das "terms"-Layout überschreibt Navigations-Bezeichnungen.
  const { tenantId, gateway } = useCustomize();
  const canCustomize = platform.permissions.has('admin.view');
  const [terms, setTerms] = useState<UiLayout>(emptyUiLayout);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsSaving, setTermsSaving] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    gateway.loadLayout(tenantId, NAV_TERMS_PAGE_KEY).then((loaded) => { if (active) setTerms(loaded); }).catch(() => {});
    return () => { active = false; };
  }, [gateway, tenantId]);

  const navLabel = (id: string, fallback: string) => textOverride(terms, navTermKey(id), fallback);

  const navigation = useMemo(() => modules.flatMap((module) => module.navigation
    .filter((item) => item.placement === 'primary')
    .map((item) => ({ item, category: module.category }))), [modules]);

  const categories = useMemo(() => [
    ...categoryOrder,
    ...navigation.map((entry) => entry.category).filter((category) => !categoryOrder.includes(category as typeof categoryOrder[number])),
  ].filter((category, index, all) => all.indexOf(category) === index)
    .map((category) => ({
      category,
      items: navigation
        .filter((entry) => entry.category === category)
        .map((entry) => entry.item)
        .sort((left, right) => left.order - right.order),
    }))
    .filter((group) => group.items.length > 0), [navigation]);

  const toggleFavorite = (route: string) => {
    setFavorites((current) => {
      const next = current.includes(route)
        ? current.filter((entry) => entry !== route)
        : current.length >= MAX_FAVORITES ? current : [...current, route];
      try { globalThis.localStorage?.setItem(FAVORITES_KEY, JSON.stringify(next)); } catch { /* Persistenz ist ein Komfort, kein Muss. */ }
      return next;
    });
  };
  // Nur Favoriten, deren Ziel im aktuellen Menü existiert (Rechte/Freigaben können Einträge entfernen).
  const favoriteItems = useMemo(
    () => favorites
      .map((route) => navigation.find((entry) => entry.item.route === route)?.item)
      .filter((item): item is (typeof navigation)[number]['item'] => Boolean(item)),
    [favorites, navigation],
  );

  const renderNavItem = (item: (typeof navigation)[number]['item']) => {
    const isFavorite = favorites.includes(item.route);
    const atLimit = !isFavorite && favorites.length >= MAX_FAVORITES;
    return (
      <div className="forq-nav-row" key={item.id}>
        <NavLink to={item.route}>
          <NavigationIconView icon={item.icon} />
          <span>{navLabel(item.id, item.label)}</span>
        </NavLink>
        <button
          type="button"
          className={`forq-nav-fav ${isFavorite ? 'is-on' : ''}`}
          disabled={atLimit}
          aria-pressed={isFavorite}
          title={isFavorite ? 'Aus Favoriten entfernen' : atLimit ? 'Maximal 5 Favoriten' : 'Zu Favoriten hinzufügen'}
          aria-label={isFavorite ? `${item.label} aus Favoriten entfernen` : atLimit ? 'Maximal 5 Favoriten möglich' : `${item.label} zu Favoriten hinzufügen`}
          onClick={() => toggleFavorite(item.route)}
        >
          <Star size={13} fill={isFavorite ? 'currentColor' : 'none'} />
        </button>
      </div>
    );
  };

  const routeLabels = useMemo(() => {
    const values = new Map<string, string>([['/complete-preview', textOverride(terms, shellTermKey('workspaces'), 'Arbeitsbereiche')]]);
    navigation.forEach(({ item }) => values.set(item.route, textOverride(terms, navTermKey(item.id), item.label)));
    return values;
  }, [navigation, terms]);

  // Gruppen für den Umbenennen-Dialog: je Kategorie das Gruppen-Label + je Eintrag das Item-Label.
  const termGroups = useMemo<NavTermGroup[]>(() => [
    { category: '__shell', groupKey: '', groupLabel: 'Bereich', items: [
      { key: shellTermKey('workspaces'), label: 'Arbeitsbereiche' },
      { key: shellTermKey('favorites'), label: 'Favoriten' },
    ] },
    ...categories.map((group) => ({
      category: group.category,
      groupKey: navGroupTermKey(group.category),
      groupLabel: categoryLabels[group.category] ?? group.category,
      items: group.items.map((item) => ({ key: navTermKey(item.id), label: item.label })),
    })),
  ], [categories]);

  const saveTerms = (overrides: Record<string, string>) => {
    setTermsSaving(true);
    setTermsError(null);
    // Nur die Navigations-Keys ersetzen; Titel-/Eyebrow-/Text-Overrides in derselben terms-jsonb erhalten.
    const preserved: Record<string, string> = {};
    for (const [key, value] of Object.entries(terms.texts)) {
      if (!key.startsWith(navTermKey('')) && !key.startsWith(navGroupTermKey('')) && !key.startsWith(shellTermKey(''))) preserved[key] = value;
    }
    gateway.saveLayout(tenantId, NAV_TERMS_PAGE_KEY, { ...terms, texts: { ...preserved, ...overrides } })
      .then((saved) => { setTerms(saved); setTermsOpen(false); })
      .catch((error: unknown) => setTermsError(error instanceof Error ? error.message : 'Die Begriffe konnten nicht gespeichert werden.'))
      .finally(() => setTermsSaving(false));
  };

  // Freie Text-Umbenennungen (Original→Ersatz) aus derselben terms-jsonb, mandantenweit auf .forq-content
  // angewandt; „Texte umbenennen"-Modus lässt jeden Inhaltstext anklicken und ersetzen.
  const textOverrides = useMemo(() => textOverrideMap(terms), [terms]);
  useApplyTextOverrides(textOverrides);
  const [textEditing, setTextEditing] = useState(false);

  const renameText = (original: string, replacement: string | null) => {
    const texts = { ...terms.texts };
    const key = `${TEXT_TERM_PREFIX}${original}`;
    if (replacement) texts[key] = replacement; else delete texts[key];
    const next = { ...terms, texts };
    setTerms(next);
    gateway.saveLayout(tenantId, NAV_TERMS_PAGE_KEY, next).then(setTerms).catch(() => {});
  };

  const currentLabel = routeLabels.get(location.pathname) ?? fallbackRouteLabel(location.pathname);

  useEffect(() => {
    setWorkspaceTabs((current) => {
      const existing = current.find((tab) => tab.route === location.pathname);
      if (existing?.label === currentLabel) return current;
      const next = existing
        ? current.map((tab) => tab.route === location.pathname ? { ...tab, label: currentLabel } : tab)
        : [...current, { route: location.pathname, label: currentLabel }];
      return next.slice(-9);
    });
  }, [currentLabel, location.pathname]);

  const closeWorkspace = (route: string) => {
    const next = workspaceTabs.filter((tab) => tab.route !== route);
    setWorkspaceTabs(next);
    if (route === location.pathname) {
      navigate(next.at(-1)?.route ?? '/complete-preview');
    }
  };

  const openCurrentInWindow = () => {
    window.open(`${location.pathname}${location.search}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="forq-shell">
      <aside className="forq-sidebar" aria-label="FORQ Navigation">
        <div className="forq-brand">
          <img src="/forq-wordmark.svg" alt="FORQ" width={1340} height={384} />
        </div>

        <div className="forq-tenant-compact">
          <span className="forq-tenant-compact__mark">{platform.activeTenant.shortName}</span>
          <div>
            <small>Aktiver Mandant</small>
            <strong>{platform.activeTenant.name}</strong>
          </div>
          <label aria-label="Mandant wechseln">
            <select
              value={platform.activeTenant.id}
              onChange={(event) => platform.setActiveTenantId(event.target.value)}
              disabled={platform.permissionStatus === 'loading'}
            >
              {platform.tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>{tenant.shortName}</option>
              ))}
            </select>
            <ChevronDown size={13} />
          </label>
        </div>

        <nav className="forq-navigation">
          <NavLink to="/complete-preview" className="forq-navigation__register">
            <Boxes size={16} />
            <span>{textOverride(terms, shellTermKey('workspaces'), 'Arbeitsbereiche')}</span>
          </NavLink>
          {favoriteItems.length > 0 ? (
            <div className="forq-navigation__group forq-navigation__group--fav">
              <span className="forq-navigation__group-label"><Star size={11} fill="currentColor" /> {textOverride(terms, shellTermKey('favorites'), 'Favoriten')}</span>
              {favoriteItems.map((item) => renderNavItem(item))}
            </div>
          ) : null}
          {categories.map((group) => (
            <div className="forq-navigation__group" key={group.category}>
              <span className="forq-navigation__group-label">
                {textOverride(terms, navGroupTermKey(group.category), categoryLabels[group.category] ?? group.category)}
              </span>
              {group.items.map((item) => renderNavItem(item))}
            </div>
          ))}
        </nav>

        <div className="forq-sidebar__footer">
          <span className="forq-status-dot" />
          <span>{platform.mode === 'demo' ? 'Vorschau · lokale Daten' : 'Supabase verbunden'}</span>
        </div>
      </aside>

      <div className="forq-workspace">
        <header className="forq-topbar">
          <button className="forq-icon-button forq-mobile-menu" type="button" aria-label="Navigation öffnen">
            <Menu size={18} />
          </button>
          <button type="button" className="forq-search" onClick={() => onOpenCommand?.()} aria-label="Global suchen (Strg K)">
            <Search size={16} />
            <span className="forq-search__label">Suchen oder Aktion ausführen …</span>
            <kbd>Ctrl K</kbd>
          </button>
          <div className="forq-topbar__actions">
            {platform.permissions.has('forqy.view') && (
              <button className="forq-icon-button" type="button" aria-label="FORQY – Hilfe und Assistent" onClick={() => onOpenForqy?.()}>
                <Sparkles size={17} />
              </button>
            )}
            {onReportBug && (
              <button
                className="forq-icon-button"
                type="button"
                aria-label="Fehler melden"
                title="Fehler melden"
                onClick={onReportBug}
              >
                <Bug size={17} />
              </button>
            )}
            <button className="forq-icon-button" type="button" aria-label="Aufgaben und Benachrichtigungen" onClick={() => onOpenSignal?.()}>
              <Bell size={17} />
              {signalCount > 0 && <span className="forq-notification-count">{signalCount}</span>}
            </button>
            <details className="forq-settings">
              <summary className="forq-icon-button" aria-label="Darstellung konfigurieren">
                <Settings2 size={17} />
              </summary>
              <div className="forq-settings__panel">
                <span className="forq-eyebrow">Arbeitsplatz</span>
                <h3>Darstellung</h3>
                <label>
                  Dichte
                  <select value={platform.shellMode} onChange={(event) => platform.setShellMode(event.target.value as 'standard' | 'compact')}>
                    <option value="standard">Standard</option>
                    <option value="compact">Kompakt</option>
                  </select>
                </label>
                <label>
                  Kontrast
                  <select value={platform.themeMode} onChange={(event) => platform.setThemeMode(event.target.value as 'dark-red' | 'high-contrast')}>
                    <option value="dark-red">FORQ Professional</option>
                    <option value="high-contrast">Hoher Kontrast</option>
                  </select>
                </label>
                {platform.permissions.has('admin.view') ? (
                  <>
                    <span className="forq-eyebrow forq-settings__section">Anpassen</span>
                    <button
                      type="button"
                      className={`forq-settings__customize ${platform.customizing ? 'is-on' : ''}`}
                      onClick={() => platform.setCustomizing(!platform.customizing)}
                    >
                      <Pencil size={14} />
                      {platform.customizing ? 'Bearbeiten-Modus beenden' : 'Seiten bearbeiten'}
                    </button>
                    <p className="forq-settings__customize-hint">Kacheln je Mandant ordnen, aus-/einblenden und benennen.</p>
                    <button
                      type="button"
                      className="forq-settings__customize forq-settings__terms"
                      onClick={() => { setTermsError(null); setTermsOpen(true); }}
                    >
                      <Type size={14} /> Begriffe umbenennen
                    </button>
                    <p className="forq-settings__customize-hint">Bezeichnungen der Navigation je Mandant anpassen (z. B. „Vorgänge" → „Aufträge").</p>
                    <button
                      type="button"
                      className={`forq-settings__customize forq-settings__terms ${textEditing ? 'is-on' : ''}`}
                      onClick={(event) => { setTextEditing((value) => !value); event.currentTarget.closest('details')?.removeAttribute('open'); }}
                    >
                      <Type size={14} /> {textEditing ? 'Text-Modus beenden' : 'Texte umbenennen'}
                    </button>
                    <p className="forq-settings__customize-hint">Beliebige Texte im Inhalt anklicken und umbenennen (Überschriften, Labels …), mandantenweit.</p>
                  </>
                ) : null}
              </div>
            </details>
            <div className="forq-user">
              <span>{platform.user.displayName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()}</span>
              <div>
                <strong>{platform.user.displayName}</strong>
                <small>{platform.user.role}</small>
              </div>
            </div>
            {platform.canSignOut && (
              <button className="forq-secondary-action" type="button" onClick={() => void platform.signOut()}>
                Abmelden
              </button>
            )}
          </div>
        </header>

        <div className="forq-workspace-tabs" aria-label="Geöffnete Arbeitsbereiche">
          <div className="forq-workspace-tabs__items">
            {workspaceTabs.map((tab) => (
              <div className={`forq-workspace-tab ${tab.route === location.pathname ? 'is-active' : ''}`} key={tab.route}>
                <NavLink to={tab.route}>{tab.label}</NavLink>
                <button type="button" aria-label={`${tab.label} schließen`} onClick={() => closeWorkspace(tab.route)}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
          <button className="forq-window-action" type="button" onClick={openCurrentInWindow} title="In eigenem FORQ-Fenster öffnen">
            <ExternalLink size={14} />
            <span>Neues Fenster</span>
          </button>
        </div>

        {platform.permissionError && <div className="forq-permission-error">{platform.permissionError}</div>}
        <main className="forq-content">{children}</main>
        {platform.customizing && platform.permissions.has('admin.view') ? (
          <div className="forq-customize-bar" role="status">
            <Pencil size={15} />
            <span className="forq-customize-bar__text">
              <strong>Bearbeiten-Modus</strong> · Kacheln ziehen, aus-/einblenden, Texte ändern. Gilt für den ganzen Mandanten.
            </span>
            <button type="button" className="forq-customize-bar__done" onClick={() => platform.setCustomizing(false)}>
              Fertig
            </button>
          </div>
        ) : null}
        {textEditing && platform.permissions.has('admin.view') ? (
          <div className="forq-customize-bar forq-customize-bar--text" role="status">
            <Type size={15} />
            <span className="forq-customize-bar__text">
              <strong>Text-Modus</strong> · Klicke einen beliebigen Text im Inhalt, um ihn umzubenennen. Gilt für den ganzen Mandanten.
            </span>
            <button type="button" className="forq-customize-bar__done" onClick={() => setTextEditing(false)}>
              Fertig
            </button>
          </div>
        ) : null}
      </div>
      {canCustomize ? (
        <NavTermsDialog
          open={termsOpen}
          groups={termGroups}
          terms={terms}
          saving={termsSaving}
          error={termsError}
          onClose={() => setTermsOpen(false)}
          onSave={saveTerms}
        />
      ) : null}
      {canCustomize ? (
        <TextRenameLayer active={textEditing} overrides={textOverrides} onRename={renameText} />
      ) : null}
    </div>
  );
}
